/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _4 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Введение</h2>
    <p className={styles.description}>
      При написании кода, важно не просто знать какой символ или конструкцию можно использовать, но в первую очередь
      необходимо понимать терминологию и составляющие исходного кода. В этой секции нам не важно понимание как что
      работает, мы лишь познакомимся с терминологией и синтаксисом языка.
    </p>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Инструкция</h2>
      <p className={styles.description}>
        <b>Инструкция (statement)</b> — это связанный набор слов и символов из синтаксиса языка, которые объединяются,
        чтобы выразить одну идею, одну инструкцию для машины.
      </p>
      <CopyBlock text={`a = b * 2;`} language="js" showLineNumbers={false} theme={ocean} />
      <p className={styles.description}>
        Это пример инструкции. В JavaScript можно различить инструкцию по точке с запятой в конце. Точку с запятой в
        данном случае можно сравнить с точкой в конце предложения вашего родного языка, она символизирует о конце
        предложения.
      </p>
      <p className={styles.notificationStudy}>
        Завершение инструкции точкой с запятой не требуется, однако рекомендуется всегда ее ставить. Это простое правило
        сделает код более понятным и поможет избежать многих ошибок.
      </p>

      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>a</code> и <code className={styles.code}>b</code> — переменные (как в
          алгебраическом уравнении), это хранилища данных, которые использует программа. Переменная состоит из
          идентификатора (имени) и связанного с ним значения.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>2</code> — просто число. Это называется значением литерала (literal value), так
          как оно не хранится в переменной.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>=</code> и <code className={styles.code}>*</code> — операторы, производят
          действия над значениями и переменными.
        </li>
      </ul>

      <p className={styles.description}>
        Представим, что переменная <code className={styles.code}>b</code> уже хранит число{' '}
        <code className={styles.code}>10</code>. Тогда эта инструкция говорит машине:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Пойди найди переменную с идентификатором <code className={styles.code}>b</code> и спроси, какое у нее сейчас
          значение.
        </li>
        <li className={styles.list__item}>
          Подставь значение переменной <code className={styles.code}>b</code> в утверждение на место{' '}
          <code className={styles.code}>b</code>
        </li>
        <li className={styles.list__item}>
          Умножь <code className={styles.code}>10</code> на <code className={styles.code}>2</code>
        </li>
        <li className={styles.list__item}>
          После чего, запиши результат вычисления выражения правой части в переменную
          <code className={styles.code}>a</code>.
        </li>
      </ul>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - Выражение</h2>
      <p className={styles.description}>
        Инструкции состоят из частей, как в любом языке предложения состоят из фраз и эти фразы называются выражениями.
      </p>
      <p className={styles.description}>
        <b>Выражение (expression)</b> — ссылка на переменную или значение, или на набор переменных и значений в
        сочетании с операторами.
      </p>
      <CopyBlock text={`[  [ a ] = [  [ b ] * [2]  ]  ]`} language="javascript" showLineNumbers theme={ocean} />
      <p className={styles.description}>
        Инструкция из примера выше содержит 5 выражений, для визуализации выделены квадратными скобками (
        <i>это не синтаксис языка</i>):
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>[2]</code> — выражение значения литерала.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>[b]</code> и <code className={styles.code}>[a]</code> — выражения переменной,
          означают необходимость подставить значение переменной, но только в том случае, если переменная стоит в правой
          части выражения присваивания.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>[b * 2]</code> — арифметическое выражение умножения.
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>[a = b * 2]</code> — выражение присваивания. В нашем случае указывает на
          необходимость вычисления правой части выражения и присваивания результата переменной{' '}
          <code className={styles.code}>a</code> в левой части выражения.
        </li>
      </ul>
      <p className={styles.description}>
        Так же есть выражения вызова, выражения сравнения и т. д. Мы не будем сейчас рассматривать их все, нам важно
        понимать из каких частей состоит исходный код и как правильно его читать.
      </p>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>4 - Интерфейс</h2>
      <p className={styles.description}>
        Когда мы подходим к автомату с кофе или садимся за руль автомобиля, существует некоторый набор элементов
        управления, с которым можно взаимодействовать. В программировании это называется интерфейс.
      </p>
      <p className={styles.description}>
        <b>Интерфейс</b> — это набор свойств и методов сущности, доступных для использования.
      </p>
      <p className={styles.description}>
        Сейчас важен именно синтаксис и терминология, так как они идентичны для любых данных.
      </p>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.1 - Свойство</h3>
        <p className={styles.description}>
          У нас с вами есть свойства: рост, вес, цвет глаз, то есть какие-то описательные характеристики. Так же и у
          данных есть свойства, к примеру у строки есть свойство ее длины. Синтаксис обращения к свойству очень простой.
        </p>
        <CopyBlock text={`сущность.имя_свойства`} language="javascript" showLineNumbers theme={ocean} />
        <p className={styles.description}>
          Для наглядности, обратимся к свойству строки <code className={styles.code}>length</code>, которое содержит
          кол-во символов строки.
        </p>
        <CopyBlock text={`'JavaScript is awesome'.length;`} language="javascript" showLineNumbers theme={ocean} />
      </section>

      <section className={styles.subsection}>
        <h3 className={styles.h3}>4.2 - Метод</h3>
        <p className={styles.description}>
          Метод - это вызов действия, например <code className={styles.code}>присесть</code> или{' '}
          <code className={styles.code}>плавать</code>, то есть какая-то активная операция. Так же и у данных есть свои
          методы, например можно добавить или удалить элементы из коллекции, перевести строку в разный регистр и т. д.
          Синтаксис вызова метода очень похож на обращение к свойству, но в конце добавляется пара скобок.
        </p>
        <CopyBlock text={`сущность.имя_метода()`} language="javascript" showLineNumbers={false} theme={ocean} />
        <p className={styles.description}>
          Для примера обратимся к методу строки <code className={styles.code}>toUpperCase()</code>, который сделает все
          буквы заглавными.
        </p>
        <CopyBlock
          text={`'JavaScript is awesome'.toUpperCase();`}
          language="javascript"
          showLineNumbers
          theme={ocean}
        />
      </section>
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>5 - Строгий режим</h2>
      <p className={styles.description}>
        <b>Строгий режим</b> — новая возможность в спецификации ECMAScript 5, которая позволяет переводить скрипт в
        режим полного соответствия современному стандарту. Это предотвращает определенные ошибки, такие как
        использование небезопасных конструкций.
      </p>
      <p className={styles.description}>
        Для того чтобы перевести скрипт в строгий режим, достаточно указать директиву в начале js-файла. Всегда пишите
        код в строгом режиме.
      </p>
      <CopyBlock
        text={`'use strict';
// дальше идет весь код файла`}
        language="javascript"
        showLineNumbers={false}
        theme={ocean}
      />
      <a
        className={styles.link}
        rel="noopener noreferrer"
        target="_blank"
        href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Strict_mode"
      >
        Strict mode на MDN
      </a>
    </section>
  </section>
))
